import { useState, useEffect, useCallback } from 'react';
import { isApiError } from '@aiptyg/types';
import * as invitationService from '../services/invitation.service';
import type { InvitationCodeDTO } from '../types/invitation.types';
import { useToastStore } from '../../../store/toast.store';

const ERROR_MAP: Record<string, string> = {
  admin_role_required: '无管理员权限',
  network_error: '网络连接失败，请检查网络',
  'user not found': '用户不存在',
  'invalid invite code': '邀请码格式错误，应为 6 位数字',
  'invalid invite code status filter': '无效的状态筛选',
  'invalid invite code operation': '无效的邀请码操作',
  'invite code already exists': '邀请码已存在',
  'invite code unavailable': '邀请码不可用',
  'invite code cannot be changed after downstream or business data exists': '该用户已有下游业务数据，不可更换邀请码',
};

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    return ERROR_MAP[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useInvitationCodes() {
  const [items, setItems] = useState<InvitationCodeDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [status, setStatusRaw] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const addToast = useToastStore((s) => s.addToast);

  const load = useCallback(async (p: number, s: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await invitationService.listInvitationCodes({
        page: p, pageSize, status: s,
      });
      setItems(result.items);
      setTotal(result.total);
      setPage(result.page);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    load(page, status);
  }, [load, page, status]);

  const setStatus = useCallback((s: string) => {
    setStatusRaw(s);
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setStatusRaw('');
    setPage(1);
  }, []);

  const reload = useCallback(() => {
    load(page, status);
  }, [load, page, status]);

  const createCodes = useCallback(async (
    codes: string[],
    generateCount: number,
    note: string,
  ) => {
    setSubmitting(true);
    try {
      const result = await invitationService.createInvitationCodes({
        codes,
        generate_count: generateCount,
        note,
      });
      addToast('success', `成功创建 ${result.count} 个邀请码`);
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const assignCode = useCallback(async (
    userId: number,
    inviteCode: string,
    note: string,
  ) => {
    setSubmitting(true);
    try {
      const result = await invitationService.assignInvitationCode({
        user_id: userId,
        invite_code: inviteCode,
        note,
      });
      if (result.replaced) {
        addToast('success', `分配成功，已替换原邀请码 ${result.previous_code}`);
      } else {
        addToast('success', '邀请码分配成功');
      }
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSubmitting(false);
    }
  }, [addToast, reload]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    items, total, page, pageSize, totalPages,
    status, loading, error, submitting,
    setPage, setStatus, resetFilters, reload,
    createCodes, assignCode,
  };
}
